<script setup lang="ts">
import ToggleSwitch from 'primevue/toggleswitch';
import GenericLoader from '~/components/user-interface/generic-loader.vue';
import { type Dayjs } from 'dayjs';
import type { ITransactionType } from '~/interfaces/dto/wallet/transactions';
// import { useDebounceFn } from '@vueuse/core'

const { $t, $dayjs } = useNuxtApp();
const { $walletService } = useServices();
const siteStore = useSiteStore();
const { transactionsFilter } = useTransactionsFilter();

const toggle = ref<typeof ToggleSwitch>();
const isLoading = ref<boolean>(true);
// const isSearching = ref<boolean>(false);     TODO:uncomment this when search by game name ready
const dateRange = ref<{ startDate: Dayjs; endDate: Dayjs } | null>({});
const gameName = ref<string>(transactionsFilter.value?.gameName);
const searchResults = ref<string[]>([]);
const showPayoutsOnly = ref<boolean>(transactionsFilter.value?.showPayoutsOnly);
const selectedTransactionTypes = ref<ITransactionType[]>(null);
const transactionTypes = ref<ITransactionType[]>([]);

const today = $dayjs();
const maxDate = ref<Dayjs>($dayjs());
const minDate = ref<Dayjs>(
  today.endOf('day').subtract(1, 'month').startOf('day'),
);

const disableButtons = computed(
  () =>
    (!!dateRange.value?.startDate && !!dateRange.value?.endDate) ||
    !!gameName.value ||
    showPayoutsOnly.value ||
    (!!selectedTransactionTypes.value &&
      selectedTransactionTypes?.value?.length > 0),
);

/*TODO: uncomment when search by game name is ready
  const handleSearchGame = useDebounceFn(async () => {
  const search = gameName.value.trim() as string;
  if (search.length < 3) return;
  isSearching.value = true;

  const response = await $gameService.fetchSearchedGames({
    search,
    languageCode: 'en-US',
    channel: 'WebDesktop',
    skip: 0,
    limit: 0,
    currency: 'USD',
  });

  if (response.total < 1) return;
  const allGames = response?.verticals?.find(
    (vertical) => vertical?.vertical?.toLowerCase() === 'all',
  ).data;

  searchResults.value = allGames.map((game) => game.name);
  isSearching.value = false;
}, 500);
*/
/*TODO: uncomment when search by game name is ready
const handleSearchBlur = () => {
  if (gameName.value === '') return;
  const foundGameName = searchResults.value.find(
    (name) => name.toLowerCase() === gameName.value.toLowerCase().trim(),
  );
  if (foundGameName) {
    gameName.value = foundGameName;
    return;
  }
  gameName.value = '';
  searchResults.value = [];
};*/

const handleReset = () => {
  gameName.value = '';
  dateRange.value = null;
  showPayoutsOnly.value = false;
  selectedTransactionTypes.value = null;
  searchResults.value = [];
  maxDate.value = $dayjs();
  minDate.value = today.endOf('day').subtract(30, 'day').startOf('day');
  transactionsFilter.value = null;
};

const handleConfirm = () => {
  const transactionTypeCategoryIds = selectedTransactionTypes?.value?.map(
    (transactionType) => transactionType.transactionTypeCategoryId,
  );
  transactionsFilter.value = {
    ...(showPayoutsOnly.value && { showPayoutsOnly: showPayoutsOnly.value }),
    ...(!!dateRange.value?.startDate && {
      dateFrom: dateRange.value.startDate.utc(true).toISOString(),
    }),
    ...(!!dateRange.value?.endDate && {
      dateTo: dateRange.value.endDate.utc(true).toISOString(),
    }),
    ...(!!selectedTransactionTypes.value &&
      selectedTransactionTypes.value.length > 0 && {
        transactionTypeCategoryIds,
      }),
    // ...(!!gameName.value && { gameName: gameName.value }),   TODO: add when search by game name is ready
  };
  siteStore.deactivateModal();
};

const handleRemoveSelectedTransactionType = (
  transactionTypeCategoryId: string,
) => {
  selectedTransactionTypes.value = selectedTransactionTypes.value.filter(
    (transactionType) =>
      transactionType.transactionTypeCategoryId !== transactionTypeCategoryId,
  );
};

const handleDateRangeChange = (range: { startDate: Dayjs; endDate: Dayjs }) =>
  (dateRange.value = { ...range });

const handleShowPayoutsOnly = (toggleValue: boolean) => {
  if (toggleValue)
    selectedTransactionTypes.value = transactionTypes.value.filter(
      (transaction) =>
        transaction.description === $t('transaction-type-payout'),
    );
  else
    selectedTransactionTypes.value = selectedTransactionTypes.value.filter(
      (transaction) =>
        transaction.description !== $t('transaction-type-payout'),
    );
};

/* TODO: uncomment when search by game name is ready
watch(gameName, handleSearchGame);
*/

const isPayoutsOnlyInSelectedTransactions = computed(() =>
  selectedTransactionTypes.value?.some(
    (transaction) => transaction.description === $t('transaction-type-payout'),
  ),
);

watchEffect(() => {
  if (selectedTransactionTypes.value?.length === 1) {
    showPayoutsOnly.value =
      selectedTransactionTypes.value[0]?.description ===
      $t('transaction-type-payout');
  } else if (
    (!isPayoutsOnlyInSelectedTransactions.value &&
      showPayoutsOnly.value &&
      toggle.value?.checked) ||
    selectedTransactionTypes.value?.length > 1
  )
    showPayoutsOnly.value = false;
});

onBeforeMount(async () => {
  const response = await $walletService.fetchTransactionCategories();
  if (response?.isSuccessful)
    transactionTypes.value = response.data.transactionTypeCategories
      .map((transactionType) => ({
        transactionTypeCategoryId: transactionType.transactionTypeCategoryId,
        description: $t(
          `transaction-type-${transactionType.description.toLowerCase()}`,
        ),
      }))
      .filter(
        (transactionType) =>
          transactionType.description !== 'transaction-type-system' &&
          transactionType.description !== 'transaction-type-transfer',
      );
  selectedTransactionTypes.value = transactionTypes?.value.filter(
    (transaction) =>
      transactionsFilter.value?.transactionTypeCategoryIds?.some(
        (selectedId) => transaction.transactionTypeCategoryId === selectedId,
      ),
  );

  if (!!transactionsFilter?.value)
    ['dateFrom', 'dateTo'].forEach((dateVariable) => {
      if (
        transactionsFilter?.value[dateVariable] &&
        typeof transactionsFilter?.value[dateVariable] === 'string'
      )
        switch (dateVariable) {
          case 'dateFrom':
            dateRange.value.startDate = $dayjs(
              transactionsFilter.value[dateVariable],
            )
              .utc(true)
              .startOf('day');
            break;
          case 'dateTo':
            dateRange.value.endDate = $dayjs(
              transactionsFilter.value[dateVariable],
            )
              .utc(true)
              .endOf('day');
            break;
        }
    });
  isLoading.value = false;
});
</script>

<template>
  <div class="flex flex-col p-4 pt-2 gap-4">
    <GenericLoader v-if="isLoading" standalone />
    <div v-if="!isLoading">
      <AccountHistoryFilterDatePicker
        v-model="dateRange"
        :min-date="minDate"
        :max-date="maxDate"
      >
        <template #date-range-buttons>
          <AccountHistoryFilterDateRangeButtons
            :date-from="dateRange?.startDate"
            :date-to="dateRange?.endDate"
            @date-range-change="handleDateRangeChange($event)"
          />
        </template>
      </AccountHistoryFilterDatePicker>

      <div class="flex w-full items-center justify-between py-2">
        <span class="text-base-priority text-xs capitalize">
          {{ $t('show-payouts-only') }}
        </span>
        <ToggleSwitch
          ref="toggle"
          v-model="showPayoutsOnly"
          @update:model-value="handleShowPayoutsOnly($event)"
        />
      </div>
      <!--      TODO: uncomment this when search by game name is ready-->
      <!--      <div class="flex flex-col gap-1.5">-->
      <!--        <span class="text-base-priority text-xs">-->
      <!--          {{ $t('filter-by-game-name') }}-->
      <!--        </span>-->
      <!--        <Select-->
      <!--          v-model="gameName"-->
      <!--          editable-->
      <!--          :options="searchResults"-->
      <!--          :placeholder="$t('enter-game-name')"-->
      <!--          :pt="searchableSelectPassThrough"-->
      <!--          :loading="isSearching"-->
      <!--          @blur="handleSearchBlur"-->
      <!--        />-->
      <!--      </div>-->
      <div class="flex flex-col gap-3">
        <AccountHistoryTransactionFilterTypeMultiSelect
          v-model:selected-transaction-types="selectedTransactionTypes"
          :transaction-types="transactionTypes"
        />
        <AccountHistoryTransactionTypeChipCarousel
          :selected-transaction-types="selectedTransactionTypes"
          @remove-selected="handleRemoveSelectedTransactionType($event)"
        />
      </div>
    </div>
  </div>
  <AccountHistoryFilterModalActions
    :disable-buttons="disableButtons"
    @reset="handleReset"
    @confirm="handleConfirm"
  />
</template>
<style>
[data-pc-section='pcoptioncheckbox.icon'] > path {
  @apply fill-light-200;
}
</style>
